import {
  applicationStore,
  freeBetStore,
  gameConfigStore,
  gameResultStore,
  loadingStore,
  // platformMessageStore,
  popupStore,
  roundInfoStore,
  userInfoStore,
  BetResponseCode,
  DefaultQueryParams,
  isMobile,
  notificationStore,
  parseNumberByPrecision,
  parseQuery,
  tableBetHistoryStore,
} from '@pascal/pascal-core';
import { gameStore } from '../store/gameStore';
import { doBetStore } from '../store/doBetStore';
import { cardsStore } from '../store/cardsStore';
import { gameLiveConnection } from './GameConnection';
import { DECISION_TYPE } from '../enums/decisionTypes';
import {
  DECISION_ACCEPTED_CODE,
  DECISION_RESULT_EVENTS,
  DOUBLE_BET_CODE,
  INSURANCE_TYPE,
  SOCKET_INVOKE_TARGETS,
  SPLIT_BEY_CODE,
} from '../enums/constants';
import { DARK_CARD_STATUS, DEALER_CARD, OPENED_CARD_STATUS } from '../enums/cardStatus';
import { worker } from '../index';
import { roundTimeStore } from '../store/roundTimeStore';
import { limitsAndPayoutsStore } from '../store/limitsAndPayoutsStore';
import { MobileShuffleAnimation, WebShuffleAnimation } from '../core/constants/events';
import { CARDS } from '../enums/cardTypes';
import { winningAmountStore } from '../store/winningAmountStore';

const animationsTimeOutId = [];
let shuffleAnimationTimeOutId;
let endShuffleAnimationTimeOutId;

class TurboGameService {
  onGetInitialStateResult({
    rate,
    limits,
    balance,
    precision,
    roundInfo,
    cardResults,
    openedCards,
    currencyCode,
    userFreebets,
    isTestPlatform,
    underMaintenance,
    cuttingCardPlace,
    gameConfiguration,
  }) {
    // platformMessageStore.setIsUsedGetBalanceBySocketInvoke(false);
    freeBetStore.setIsFreeChip(true);
    tableBetHistoryStore.setFetchPaths({ ThirdStep: '/betDetails' });
    tableBetHistoryStore.setFetchParamNames({ Id: 'RoundId' });
    this.resetInitialData();
    if (underMaintenance) {
      popupStore.setReloadFrameForUnderMaintenanceOnRoundStart(true);
      popupStore.startUnderMaintenanceFunctional(underMaintenance);
    }
    const {
      [DefaultQueryParams.CurrencySymbol]: currencySymbol,
      [DefaultQueryParams.UserId]: userId,
    } = parseQuery(window.location.search);
    let alreadyOpenedCards = [...openedCards];
    let currentRoundCards = [];
    if (roundInfo?.roundId) {
      currentRoundCards = alreadyOpenedCards.filter((item) => item.roundId === roundInfo.roundId);
      alreadyOpenedCards = alreadyOpenedCards.filter((item) => item.roundId !== roundInfo.roundId);
    }
    const cardsResultWithoutOpenedCards = cardResults.reduce((acc, cur) => {
      const openCard = currentRoundCards.find((currentCard) => currentCard?.index === cur?.index);
      if (!openCard) {
        acc.push(cur);
      }
      return acc;
    }, []);
    const darkCards = [...currentRoundCards, ...cardsResultWithoutOpenedCards]
      .reverse()
      .sort((a, b) => b.index - a.index);
    cardsStore.setOpenedCardsCount(openedCards.length);
    gameResultStore.setAdditionalResults(alreadyOpenedCards.reverse());
    gameResultStore.setGameResults(darkCards);
    userInfoStore.setUserInfo({
      rate,
      userId,
      balance,
      precision,
      currencyCode,
      currencySymbol,
      demoBalance: 10000,
    });
    applicationStore.setIsDemo(isTestPlatform);
    applicationStore.setIsTestPlatform(isTestPlatform);
    const { limitsAndPayouts, duration } = gameConfiguration;
    limitsAndPayoutsStore.setLimitsAndPayouts(limitsAndPayouts);
    gameStore.setDecisionDuration(duration);
    cardsStore.setBoxCount(3);
    gameConfigStore.setLimits(limits);
    doBetStore.setQuickBets(limits.quickBets);
    if (!freeBetStore.activeFreeBet.id) {
      doBetStore.setSelectedChip(limits.quickBets[0]);
    }
    cardsStore.setCuttingCardPlace(cuttingCardPlace);
    cardsStore.setIsCuttingCardOpen(cuttingCardPlace < 0);
    loadingStore.updateLoading({ isGameInitialLoaded: true });
    if (roundInfo) {
      const { userInitialState, dealerCards, possibleBetActions, roundId, dealerCardsPoint } =
        roundInfo;
      if (roundId) {
        roundInfoStore.setRoundId(roundId);
      }
      if (userInitialState) {
        if (userInitialState.bets) {
          const { bets, sideBetOdds } = userInitialState;
          const freeBetId =
            bets.betsInfo[Object.keys(bets.betsInfo)[0]].boxBets[0].mainBet?.freeBetId;
          if (freeBetId) {
            freeBetStore.setAwardedFreeBet(userFreebets.find((item) => item.id === freeBetId));
          }
          cardsStore.handleInitialStateByHand(bets.betsInfo);
          doBetStore.handleInitialBetsModel(bets.betsInfo);
          cardsStore.setDealerCardsInfo(dealerCards, dealerCardsPoint);
          if (bets?.insuranceBet) {
            gameStore.setIsInsuranceLose(true);
            doBetStore.updateInsuranceAmount();
          }
          Object.keys(sideBetOdds).forEach((item) => {
            sideBetOdds[item].calculatedSideBets = bets.betsInfo[item].boxBets[0].mainBet.sideBets;
          });
          doBetStore.setSideBetsInfo(sideBetOdds);
          gameStore.setIsBetsClosed(true);
        }
      }
      if (possibleBetActions) {
        gameStore.setActiveBox(possibleBetActions.boxIndex);
        gameStore.setPossibleActions(possibleBetActions.possibleBetActions);
        gameStore.setActiveBetId(possibleBetActions.betId);
        this.sendWorker(Math.round(duration - possibleBetActions.decisionSecond));
        gameStore.updateIsShowDecision(true);
      }
    } else {
      doBetStore.setDefaultAmountOnBox();
    }
    popupStore.toggleReconnect(false);
    popupStore.onConnectionError(false);
    popupStore.close();
    gameStore.setIsHandleInitialStateEnded(true);
  }

  combineDecisionEvents = {
    [DECISION_RESULT_EVENTS.HitResult]: this.handleHitResult.bind(this),
    [DECISION_RESULT_EVENTS.StandResult]: this.handleStandResult.bind(this),
    [DECISION_RESULT_EVENTS.SplitResult]: this.handleSplitResult.bind(this),
    [DECISION_RESULT_EVENTS.DoubleResult]: this.handleDoubleResult.bind(this),
    [DECISION_RESULT_EVENTS.SurrenderResult]: this.handleSurrenderResult.bind(this),
    [DECISION_RESULT_EVENTS.InsuranceResult]: this.handleInsuranceResult.bind(this),
    [DECISION_RESULT_EVENTS.InsuranceInfoResult]: this.handleInsuranceInfoResult.bind(this),
  };

  handleDoBetResult({ responseCode, bets, balance }) {
    const { isDemo } = applicationStore;
    popupStore.setReloadFrameForUnderMaintenanceOnRoundStart(false);
    if (responseCode === DECISION_ACCEPTED_CODE) {
      const { freeBetId } = bets[0];
      const totalBetAmount = bets
        .map((item) => item.betData.markets)
        .flat(1)
        .reduce((acc, current) => acc + current.amount, 0);
      doBetStore.setReBet(doBetStore.lastBetModel.betModel);
      doBetStore.setReBetAmount(totalBetAmount);
      gameStore.setIsBetsClosed(true);
      if (!freeBetId) {
        if (isDemo) {
          userInfoStore.setDemoBalance(userInfoStore.balance - doBetStore.betsTotalAmount);
        } else {
          userInfoStore.setBalance(balance);
        }
      }
      roundInfoStore.setRoundId(bets[0].roundId);
    } else {
      gameStore.setIsBetsClosed(false);
    }
    notificationStore.setNotificationType(responseCode);
    setTimeout(() => notificationStore.setNotificationType(null), 1000);
  }

  handleRoundFin({ data: { bets, shuffleCardsInfo } }) {
    const { activeFreeBet, freeBetTotalWinCalculator } = freeBetStore;
    const totalWinAmount = bets.reduce((acc, cur) => {
      acc = parseNumberByPrecision(1, acc + cur.winAmount, +userInfoStore.precision);
      return acc;
    }, 0);

    const freeBetId = bets?.[0]?.freeBetId;
    // const totalAmount = bets.reduce((acc, cur) => {
    //   acc = parseNumberByPrecision(1, acc + cur.amount, +userInfoStore.precision);
    //   return acc;
    // }, 0);

    if (totalWinAmount) {
      if (freeBetId) {
        freeBetTotalWinCalculator(totalWinAmount);
      }
      winningAmountStore.setWinningAmount(totalWinAmount);
      winningAmountStore.setLastWin(totalWinAmount);
    }
    cardsStore.updateWinningAmountByBox(bets);
    doBetStore.updateWinningAmountByRoundResult(bets);
    gameStore.updateIsShowDecision(false);
    gameStore.setActiveBetId(null);
    gameStore.setActiveBox(null);
    gameStore.setActiveHandByBox(null);
    gameStore.setActiveAnimationBox(null);
    if (applicationStore.isDemo) {
      userInfoStore.setDemoBalance(userInfoStore.balance + totalWinAmount);
    } else {
      gameLiveConnection.getBalance();
    }
    freeBetStore.handleFreeBetEndedData();
    setTimeout(() => {
      if (gameStore.dealerInsuranceAnimationStart) {
        gameStore.setDealerInsuranceAnimationStart(false);
      }
      if (gameStore.isInsurance) {
        gameStore.setIsInsurance(null);
      }
      gameStore.setIsInsuranceLose(false);
      tableBetHistoryStore.handleBetHistoryUpdate();
      this.resetInitialData();
      this.handleFilterResultsOpenedCards();
      cardsStore.createCardsBox(cardsStore.boxCount);
      if (activeFreeBet.id && doBetStore.reBet) {
        const allBetsCount = doBetStore.reBet.reduce((acc, cur) => {
          cur.markets.forEach(() => {
            acc += 1;
          });
          return acc;
        }, 0);
        if (activeFreeBet.remainingCount - allBetsCount >= 0) {
          doBetStore.setBetsModel({ betModel: doBetStore.reBet });
        }
      } else if (doBetStore.reBetAmount < userInfoStore.balance && doBetStore.reBet) {
        doBetStore.setBetsModel({ betModel: doBetStore.reBet });
        winningAmountStore.setLastWin(0);
      } else if (!activeFreeBet.id) {
        doBetStore.setDefaultAmountOnBox();
      }
      gameStore.setIsBetsClosed(false);
      if (cardsStore.isCuttingCardOpen) {
        cardsStore.setIsCuttingCardOpen(false);
      }
      popupStore.setReloadFrameForUnderMaintenanceOnRoundStart(true);
      // if (popupStore.reloadFrameForUnderMaintenance) {
      //   iframeService.reloadFrame();
      // }
      if (shuffleCardsInfo) {
        const { cardsResult, newShuffledCards } = shuffleCardsInfo;
        this.handleShuffleStarting({ cardsResult, newShuffledCards });
      }
    }, 2500);
  }

  handleAutoStandResult({ boxIndex, handType }) {
    gameStore.updateIsShowDecision(false);
    gameStore.setIsSelectedDecision(false);
    cardsStore.updateSelectedDecisionByBox({
      boxIndex,
      hand: handType,
      isRemoveSecondPoint: true,
      decision: DECISION_TYPE.stand,
    });
  }

  handleDecisionEvents(eventName, data) {
    // if (data.roundId === roundInfoStore.roundId) {
    gameStore.updateIsShowDecision(false);
    gameStore.setIsSelectedDecision(false);
    gameStore.setIsInsurance(false);
    this.combineDecisionEvents[eventName](data);
    // }
  }

  handleAnimation(boxIndex, openedCard) {
    if (openedCard.card === CARDS.CUTTING_CARD) {
      return;
    }
    if (
      openedCard.owner === DEALER_CARD &&
      // eslint-disable-next-line no-unsafe-optional-chaining
      cardsStore.dealerCardsInfo.cards?.[cardsStore.dealerCardsInfo.cards?.length - 1]?.status ===
        DARK_CARD_STATUS
    ) {
      return;
    }
    cardsStore.updateIsStartCardImitation(true);
    if (boxIndex !== null) {
      cardsStore.updateAnimationBox(boxIndex, 'to-shoe');
      gameStore.setActiveAnimationBox(boxIndex);
      setTimeout(() => {
        cardsStore.updateAnimationBox(boxIndex, '');
      }, 50);
    } else {
      cardsStore.setDealerAnimationClassName('to-shoe');
      setTimeout(() => {
        cardsStore.setDealerAnimationClassName('');
      }, 50);
    }
    setTimeout(() => {
      cardsStore.updateIsStartCardImitation(false);
    }, 150);
  }

  handleActionResult({
    decision,
    boxIndex,
    totalPoint,
    secondPoint,
    openedCard,
    cuttingCardPlace,
    isDouble = false,
  }) {
    cardsStore.updateCards({ boxIndex, totalPoint, openedCard, isDouble, secondPoint });
    cardsStore.updateSelectedDecisionByBox({
      boxIndex,
      decision: decision,
      hand: openedCard.handType,
    });
    cardsStore.setCuttingCardPlace(cuttingCardPlace);
    this.handleAnimation(boxIndex, openedCard);
  }

  handleHitResult({ cards, responseCode }) {
    if (responseCode === DECISION_ACCEPTED_CODE) {
      this.dealingCard({ boxIndex: cards[0].boxIndex, openedCardsInfo: cards });
    }
  }

  handleStandResult({ responseCode, handType, boxIndex }) {
    if (responseCode === DECISION_ACCEPTED_CODE) {
      cardsStore.updateSelectedDecisionByBox({
        boxIndex,
        decision: DECISION_TYPE.stand,
        hand: handType,
        isRemoveSecondPoint: true,
      });
      gameStore.updateIsShowDecision(false);
    }
  }

  handleDoubleResult({ responseCode, openedCardsInfo, boxIndex, balance, freeBetId, amount }) {
    if (responseCode === DECISION_ACCEPTED_CODE) {
      doBetStore.doubleBetAmountByAction(boxIndex);
      gameStore.updateIsShowDecision(false);
      this.dealingCard({
        boxIndex,
        openedCardsInfo,
        isDouble: true,
        decision: DECISION_TYPE.double,
      });
      if (!freeBetId) {
        if (applicationStore.isDemo) {
          userInfoStore.setDemoBalance(userInfoStore.balance - amount);
        } else {
          userInfoStore.setBalance(balance);
        }
      }
    }
  }

  handleInsuranceInfoResult({ hasInsuranceAction }) {
    gameStore.setIsSelectedDecision(false);
    gameStore.setDealerInsuranceAnimationStart(true);
    gameStore.setIsInsurance(hasInsuranceAction);
    gameStore.setPossibleActions([]);
    gameStore.updateIsShowDecision(false);
  }

  handleInsuranceResult({ responseCode, isInsurance, balance }) {
    gameStore.setIsSelectedDecision(false);
    gameStore.setIsInsuranceDecisionOpened(false);
    if (responseCode === DECISION_ACCEPTED_CODE) {
      gameStore.setDealerInsuranceAnimationStart(true);
      gameStore.setIsInsurance(isInsurance);
      if (isInsurance) {
        doBetStore.updateInsuranceAmount();
        if (applicationStore.isDemo) {
          const allBoxMainBetAmounts = doBetStore.lastBetModel.betModel.reduce((acc, cur) => {
            acc += cur.markets[0].amount;
            return acc;
          }, 0);
          userInfoStore.setDemoBalance(userInfoStore.balance - allBoxMainBetAmounts);
        } else {
          userInfoStore.setBalance(balance);
        }
      }
      gameStore.setPossibleActions([]);
      gameStore.updateIsShowDecision(false);
    } else {
      doBetStore.handleNotificationCode(BetResponseCode.InvalidBet);
    }
  }

  handleSplitResult({ boxIndex, openedCardsInfo, responseCode, balance, freeBetId, amount }) {
    if (responseCode === DECISION_ACCEPTED_CODE) {
      gameStore.updateIsShowDecision(false);
      cardsStore.splitBoxByHand({ boxIndex });
      doBetStore.doubleBetAmountByAction(boxIndex);
      this.dealingCard({ boxIndex, openedCardsInfo });
      if (!freeBetId) {
        if (applicationStore.isDemo) {
          userInfoStore.setDemoBalance(userInfoStore.balance - amount);
        } else {
          userInfoStore.setBalance(balance);
        }
      }
    }
  }

  dealingCard({ boxIndex, openedCardsInfo, isDouble = false, decision = null }) {
    let time = 500;
    openedCardsInfo.forEach(({ openedCard, cuttingCardPlace, totalPoint, secondPoint }) => {
      setTimeout(() => {
        this.handleActionResult({
          isDouble,
          boxIndex,
          decision,
          totalPoint,
          secondPoint,
          openedCard,
          cuttingCardPlace,
        });
      }, time);
      time += 500;
    });
  }

  handleSurrenderResult({ boxIndex, responseCode, amount }) {
    if (responseCode === DECISION_ACCEPTED_CODE) {
      gameStore.updateIsShowDecision(false);
      cardsStore.updateSurrenderResultByBox(boxIndex);
      doBetStore.updateSurrenderAmountByBox(boxIndex, amount);
    }
  }

  handleDoBetResultValidation({ betResult: { responseCode } }) {
    doBetStore.resetBetsModels();
    notificationStore.setNotificationType(responseCode);
    setTimeout(() => notificationStore.setNotificationType(null), 1000);
    gameStore.setIsBetsClosed(false);
  }

  sendBet() {
    gameStore.setIsBetsClosed(true);
    const { [DefaultQueryParams.GameTypeId]: gameTypeId, [DefaultQueryParams.GameId]: gameId } =
      parseQuery(window.location.search);
    const { betsModels } = doBetStore;
    const { activeFreeBet } = freeBetStore;
    const betData = betsModels[betsModels.length - 1]?.betModel;
    if (betData) {
      gameLiveConnection.doBet(
        activeFreeBet.id
          ? {
              betData,
              gameId,
              gameTypeId,
              freeBetId: activeFreeBet.id,
            }
          : {
              betData,
              gameId,
              gameTypeId,
            }
      );
    }
  }

  handleDecisionTime({ betId, boxIndex, possibleActions, handType, roundId }) {
    if (roundId === roundInfoStore.roundId) {
      if (gameStore.dealerInsuranceAnimationStart) {
        gameStore.setDealerInsuranceAnimationStart(false);
        gameStore.setIsInsuranceLose(true);
      }
      if (
        possibleActions.includes(DECISION_TYPE.double) ||
        possibleActions.includes(DECISION_TYPE.split)
      ) {
        if (!applicationStore.isDemo) {
          gameLiveConnection.getBalance();
        }
      }
      if (possibleActions.includes(INSURANCE_TYPE)) {
        gameStore.setIsInsuranceDecisionOpened(true);
      }
      gameStore.setActiveHandByBox(handType);
      this.sendWorker(gameStore.decisionDuration);
      gameStore.updateIsShowDecision(true);
      gameStore.setActiveBox(boxIndex);
      gameStore.setActiveBetId(betId);
      gameStore.setPossibleActions(possibleActions);
    }
  }

  sendDecisionAction(type) {
    const { activeFreeBet } = freeBetStore;
    if (!gameStore.isSelectedDecision) {
      gameStore.setIsSelectedDecision(true);
      if (type === DOUBLE_BET_CODE || type === SPLIT_BEY_CODE) {
        if (activeFreeBet.id && !activeFreeBet.remainingCount) {
          gameStore.setIsSelectedDecision(false);
          return;
        }
        gameLiveConnection.sendDecisionAction(SOCKET_INVOKE_TARGETS[type], {
          betId: gameStore.activeBetId,
          freeBetId: activeFreeBet.id || null,
        });
      } else {
        gameLiveConnection.sendDecisionAction(SOCKET_INVOKE_TARGETS[type], gameStore.activeBetId);
      }
    }
  }

  sendInsurance(isInsurance) {
    if (!gameStore.isSelectedDecision) {
      gameStore.setIsSelectedDecision(true);
      gameLiveConnection.sendInsurance({ roundId: roundInfoStore.roundId, isInsurance });
    }
  }

  handleResultReport({ resultReportData }) {
    const {
      boxIndex,
      cuttingCardPlace,
      hasBlackJack,
      totalPoint,
      secondPoint,
      sideBetsInfo,
      roundOpenedCard,
    } = resultReportData;
    if (sideBetsInfo) {
      doBetStore.setSideBetsInfo(sideBetsInfo);
    } else {
      if (gameStore.dealerInsuranceAnimationStart) {
        gameStore.setDealerInsuranceAnimationStart(false);
      }
      cardsStore.setCuttingCardPlace(
        cuttingCardPlace < cardsStore.cuttingCardPlace
          ? cuttingCardPlace
          : cardsStore.cuttingCardPlace
      );
      this.handleAnimation(boxIndex, roundOpenedCard);
      cardsStore.updateCards({
        boxIndex,
        totalPoint,
        secondPoint,
        openedCard: roundOpenedCard,
        hasBlackJack,
      });
    }
  }

  countDown({ data: time }) {
    roundTimeStore.setTime(time);
  }

  sendWorker(timerValue) {
    if (timerValue) {
      worker.addEventListener('message', this.countDown);
      worker.postMessage(timerValue);
    } else {
      worker.removeEventListener('message', this.countDown);
    }
  }

  handleFilterResultsOpenedCards() {
    const openedCards = gameResultStore.gameResults.filter(
      ({ status }) => status === OPENED_CARD_STATUS
    );
    gameResultStore.setAdditionalResults([...openedCards, ...gameResultStore.additionalResults]);
    const results = [...gameResultStore.gameResults];
    results.length -= openedCards.length;
    gameResultStore.setGameResults(results);
  }

  handleShuffleStarting({ cardsResult, newShuffledCards }) {
    const lastAndNextResults = cardsResult.reverse().reduce(
      (acc, cur) => {
        if (cur.roundId) {
          acc.alreadyOpenedCards.push(cur);
        } else {
          acc.nextCards.push(cur);
        }
        return acc;
      },
      {
        alreadyOpenedCards: [],
        nextCards: [],
      }
    );
    gameResultStore.setGameResults(lastAndNextResults.nextCards);
    // gameResultStore.setAdditionalResults(lastAndNextResults.alreadyOpenedCards);
    const animData = isMobile() ? MobileShuffleAnimation : WebShuffleAnimation;
    cardsStore.setIsShuffle(true);
    let animationDuration = 0;
    for (let i = 0; i < animData.length; i += 1) {
      shuffleAnimationTimeOutId = setTimeout(() => {
        cardsStore.setShuffleAnimationType(animData[i].payload.current.join(' '));
      }, animationDuration);
      animationsTimeOutId.push(shuffleAnimationTimeOutId);
      animationDuration += animData[i].payload.duration;
    }
    endShuffleAnimationTimeOutId = setTimeout(() => {
      gameResultStore.setGameResults(newShuffledCards.reverse());
      gameResultStore.setAdditionalResults([]);
      cardsStore.setOpenedCardsCount(0);
      cardsStore.setIsShuffle(false);
    }, animationDuration);
    animationsTimeOutId.push(endShuffleAnimationTimeOutId);
  }

  resetInitialData() {
    winningAmountStore.setWinningAmount(null);
    roundInfoStore.setRoundId(null);
    cardsStore.resetCards();
    doBetStore.resetBetsModels();
    gameStore.setPossibleActions([]);
    cardsStore.setIsShuffle(false);
    gameStore.setIsBetsClosed(false);
    gameStore.updateIsShowDecision(false);
    gameStore.setIsInsuranceDecisionOpened(false);
  }
}

export const turboGameService = new TurboGameService();
